import { useEffect, useMemo, useRef, useState } from "react";
import {
  calculateHaklaiAcquisitionCostInShekel,
  getHaklaiCurrencyConversionDate,
  parseHaklaiUsdRate
} from "../calc/shared/haklaiCurrencyHelpers";
import { normalizeHaklaiCurrencyForDate } from "../calc/shared/haklaiConstants";
import { convertHaklaiDollarCurrency } from "../services/haklaiCurrencyService";

const HAKLAI_USD_RATE_FAILSAFE_MS = 12000;
const HAKLAI_USD_RATE_DEBOUNCE_MS = 500;

const useHaklaiUsdRate = ({ calc, setCalc }) => {
  const [usdRate, setUsdRate] = useState(null);
  const [usdRateLoading, setUsdRateLoading] = useState(false);
  const [usdRateStale, setUsdRateStale] = useState(false);
  const usdRateCacheRef = useRef(new Map());

  const preCalc = calc.preCalc || {};
  const acquisitionDetails = preCalc.acquisitionDetails || {};
  const acquisitionDate = acquisitionDetails.deduDate;
  const acquisitionCurrency = acquisitionDetails.deduCurrency;
  const acquisitionAmount = acquisitionDetails.amount;
  const testatorPassingDate = acquisitionDetails.testatorPassingDate;
  const testatorBoughtPropertyDate = acquisitionDetails.testatorBoughtPropertyDate;
  const wayOfGettingProperty = preCalc.wayOfGettingProperty;

  const conversionDate = useMemo(
    () => getHaklaiCurrencyConversionDate({
      wayOfGettingProperty,
      acquisitionDetails: {
        deduDate: acquisitionDate,
        testatorPassingDate,
        testatorBoughtPropertyDate
      }
    }),
    [
      wayOfGettingProperty,
      acquisitionDate,
      testatorPassingDate,
      testatorBoughtPropertyDate
    ]
  );

  useEffect(() => {
    const normalizedCurrency = normalizeHaklaiCurrencyForDate(
      conversionDate,
      acquisitionCurrency
    );

    if (normalizedCurrency === acquisitionCurrency) {
      return;
    }

    setCalc((prev) => ({
      ...prev,
      preCalc: {
        ...prev.preCalc,
        acquisitionDetails: {
          ...prev.preCalc.acquisitionDetails,
          deduCurrency: normalizedCurrency
        }
      }
    }));
  }, [acquisitionCurrency, conversionDate, setCalc]);

  useEffect(() => {
    if (acquisitionCurrency !== "usd") {
      setUsdRate(null);
      setUsdRateLoading(false);
      setUsdRateStale(false);
      return;
    }
    if (!conversionDate) {
      setUsdRate(null);
      setUsdRateLoading(false);
      setUsdRateStale(false);
      return;
    }

    const cache = usdRateCacheRef.current;
    if (cache.has(conversionDate)) {
      const cachedRate = cache.get(conversionDate);
      setUsdRate(cachedRate);
      setUsdRateLoading(false);
      setUsdRateStale(false);
      return;
    }

    let cancelled = false;
    setUsdRate(null);
    setUsdRateLoading(true);
    setUsdRateStale(false);
    const failSafeId = setTimeout(() => {
      if (cancelled) return;
      setUsdRate(null);
      setUsdRateLoading(false);
      setUsdRateStale(true);
    }, HAKLAI_USD_RATE_FAILSAFE_MS);
    const timeoutId = setTimeout(() => {
      convertHaklaiDollarCurrency(1, conversionDate).then((res) => {
        if (cancelled) return;
        clearTimeout(failSafeId);
        const rate = parseHaklaiUsdRate(res);
        if (rate) {
          cache.set(conversionDate, rate);
          setUsdRate(rate);
          setUsdRateLoading(false);
          setUsdRateStale(false);
          return;
        }
        setUsdRate(null);
        setUsdRateLoading(false);
        setUsdRateStale(true);
      }).catch(() => {
        if (cancelled) return;
        clearTimeout(failSafeId);
        setUsdRate(null);
        setUsdRateLoading(false);
        setUsdRateStale(true);
      });
    }, HAKLAI_USD_RATE_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      clearTimeout(failSafeId);
    };
  }, [acquisitionCurrency, conversionDate]);

  const acquisitionCostInShekel = useMemo(
    () => calculateHaklaiAcquisitionCostInShekel({
      amount: acquisitionAmount,
      currency: acquisitionCurrency || "newShekel",
      conversionDate,
      usdRate
    }),
    [
      acquisitionAmount,
      acquisitionCurrency,
      conversionDate,
      usdRate
    ]
  );

  return {
    acquisitionCostInShekel,
    conversionDate,
    usdRate,
    usdRateLoading,
    usdRateStale
  };
};

export default useHaklaiUsdRate;
